import React, { useEffect, useState } from 'react';
import { Container, CircularProgress, Button, Stack, Box, Typography } from '@mui/material';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as notificationActions from '../actions/notificationAction';
import { StoreApi } from '../api/storeApi';
import {PaymentSettings} from '../shared-components';
import useTranslation from '../hooks/useTranslation';

const PaymentSettingsContainer = (props) => {
  const [store, setStore] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();

  const loadAll = async () => {
    try {
      const [storeRes, currencyRes, stripeStatusRes] = await Promise.all([
        StoreApi.getStore(),
        StoreApi.getCurrencies(),
        StoreApi.getStripeStatus(),
      ]);
      setStore(storeRes.data);
      setCurrencies(currencyRes.data.data || currencyRes.data);
      setStripeStatus(stripeStatusRes.data);
      setLoading(false);
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.failed_to_load_store_data'), });
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    setStore((prevStore) => ({
      ...prevStore,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleConnectStripe = async () => {
    setStripeLoading(true);
    try {
      const email = store?.stripe_connect_email;
      const res = await StoreApi.createStripeAccount({ email });
      window.location.href = res.data.url;
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.failed_to_create_stripe_account'), });
    }
    setStripeLoading(false);
  };

  const handleOpenStripeDashboard = async () => {
    setDashboardLoading(true);
    // Open the tab synchronously so Safari's popup blocker doesn't kill it after the await
    const popup = window.open('about:blank', '_blank');
    try {
      const res = await StoreApi.getStripeDashboardLink();
      if (popup) {
        popup.location.href = res.data.url;
      } else {
        window.location.href = res.data.url;
      }
    } catch (error) {
      if (popup) popup.close();
      props.notifyActions.setErrorNotification({ message: t('response_messages.failed_to_open_stripe_dashboard'), });
    }
    setDashboardLoading(false);
  };

  const handleDisconnectStripe = async () => {
    try {
      await StoreApi.disconnectStripeAccount();
      const [storeRes, stripeStatusRes] = await Promise.all([
        StoreApi.getStore(),
        StoreApi.getStripeStatus(),
      ]);
      setStore(storeRes.data);
      setStripeStatus(stripeStatusRes.data);
      props.notifyActions.setSuccessNotification({ message: t('response_messages.stripe_disconnected_successfully'), });
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.failed_to_disconnect_stripe_account'), });
    }
  };

  const saveStore = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(store).forEach(([field, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(field, value);
        }
      });
      await StoreApi.updateStore(formData);
      props.notifyActions.setSuccessNotification({ message: t('response_messages.payment_settings_saved_successfully'), });
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.failed_to_save_store_settings'), });
    }
    setSaving(false);
  };

  if (loading || !store) return <CircularProgress />;

  return (
    <Container maxWidth="xl">
      <Stack spacing={3} sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={700}>{t('labels.payment_settings')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('labels.payment_settings_desc')}</Typography>
          </Box>
          <Button variant="contained" color="primary" onClick={saveStore} disabled={saving}>
            {saving ? t('labels.saving') : t('labels.save')}
          </Button>
        </Stack>
        <PaymentSettings
          store={store}
          currencies={currencies}
          onChange={handleChange}
          onStripeConnect={handleConnectStripe}
          onStripeDisconnect={handleDisconnectStripe}
          onStripeDashboard={handleOpenStripeDashboard}
          stripeLoading={stripeLoading}
          dashboardLoading={dashboardLoading}
          stripeStatus={stripeStatus}
          t={t}
        />
      </Stack>
    </Container>
  );
};

const mapDispatchToProps = (dispatch) => ({
  notifyActions: bindActionCreators(notificationActions, dispatch),
});

export default connect(null, mapDispatchToProps)(PaymentSettingsContainer);